import * as React from 'react';
import PropTypes from 'prop-types';

import { Link } from '../Link/Link';
import { HeroLayout } from '../../layouts/HeroLayout/HeroLayout';
import { Stack } from '../Stack/Stack';
import { ButtonListSection } from '../ButtonListSection/ButtonListSection';
import { HeroAuthors } from '../HeroAuthors/HeroAuthors';
import { ShareButtons } from '../ShareButtons/ShareButtons';
import { renderDateInterval } from '../../utils/date';

import * as cs from './EventPageHero.module.css';

const propTypes = {
  snippet: PropTypes.shape({
    moreWork: PropTypes.string.isRequired,
    moreEvents: PropTypes.string.isRequired,
    share: PropTypes.string.isRequired,
    topics: PropTypes.string.isRequired,
  }).isRequired,
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      about: PropTypes.string.isRequired,
      email: PropTypes.string,
      name: PropTypes.string.isRequired,
      twitter: PropTypes.string,
    }).isRequired,
  ).isRequired,
  title: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      to: PropTypes.string,
    }).isRequired,
  ).isRequired,
  coverImage: PropTypes.object,
  coverVideo: PropTypes.arrayOf(PropTypes.object),
  coverVideoLoop: PropTypes.bool,
  bgImages: PropTypes.shape({
    default: PropTypes.object,
    original: PropTypes.shape({
      desktop: PropTypes.object,
      mobile: PropTypes.object,
    }),
  }),
  isBgFullSize: PropTypes.bool,
  bgColorOverflow: PropTypes.bool,
  dateStart: PropTypes.string,
  dateStartISO: PropTypes.string,
  dateStartDay: PropTypes.string,
  dateStartMonth: PropTypes.string,
  dateEndDay: PropTypes.string,
  dateEndMonth: PropTypes.string,
  whiteBg: PropTypes.bool.isRequired,
  description: PropTypes.string.isRequired,
  opensourceProjects: PropTypes.arrayOf(PropTypes.object.isRequired),
  eventType: PropTypes.string.isRequired,
  city: PropTypes.string,
  eventTitle: PropTypes.string,
  url: PropTypes.string,
};

const EventPageHero = ({
  authors,
  dateStart,
  dateStartISO,
  dateStartDay,
  dateStartMonth,
  dateEndDay,
  dateEndMonth,
  title,
  coverImage,
  coverVideo,
  coverVideoLoop,
  bgImages,
  isBgFullSize,
  bgColorOverflow,
  whiteBg,
  snippet,
  description,
  opensourceProjects,
  tags,
  eventType,
  city,
  eventTitle,
  url,
}: any) => {
  const podcastArticle = eventTitle.startsWith('The ') ? 'On ' : 'On the ';

  const podcastDescription =
    eventTitle.endsWith('podcast') || eventTitle.endsWith('Podcast')
      ? ''
      : ' podcast';

  return (
    <HeroLayout
      bgImages={bgImages}
      isBgFullSize={isBgFullSize}
      bgColorOverflow={bgColorOverflow}
      coverImage={coverImage}
      coverVideo={coverVideo}
      coverVideoLoop={coverVideoLoop}
      title={title}
      whiteBg={whiteBg}
      subheader={
        <>
          {eventTitle ? (
            <>
              {eventType === 'podcast' ? podcastArticle : 'At '}
              {url ? (
                <Link to={url} target="_blank">
                  {eventTitle}
                </Link>
              ) : (
                eventTitle
              )}
              {eventType === 'podcast' ? podcastDescription : ''}
              {dateStart ? ', ' : ''}
            </>
          ) : (
            ''
          )}
          {dateStart ? (
            <time dateTime={dateStartISO && dateStartISO.split('T')[0]}>
              {renderDateInterval({
                dateStart,
                dateStartDay,
                dateStartMonth,
                dateEndDay,
                dateEndMonth,
              })}
            </time>
          ) : (
            ''
          )}
          {city ? <>{` in ${city}`}</> : ''}
        </>
      }
      infoStart={
        <Stack direction="vertical" gap={24}>
          <ButtonListSection entries={tags} title={snippet.topics} />

          <ShareButtons
            header={snippet.share}
            title={title}
            description={description}
            tags={tags}
            authors={authors}
            opensourceProjects={opensourceProjects}
          />
        </Stack>
      }
      infoEnd={<HeroAuthors entries={authors} />}
      infoClassName={cs.info}
      noEndDivider
    />
  );
};

EventPageHero.propTypes = propTypes;

export { EventPageHero };
